import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { LUMIO, LUMIO_CHAT, LUMIO_MOBILE, ANIM, ACQ } from '../../../../theme/tokens';
import { thinScrollbar, fadeMaskY , hoverWash } from '../../../../theme/mixins';

/* The resting gap between the last message and the composer when scrolled to the
 * very end of the thread (user-requested 50px). Added to the composer's measured
 * height as the thread's bottom padding. */
const THREAD_END_GAP = 50;

/* Length of the fade-mask dissolve just inside the header/footer. The thread's
 * top padding is the header height + this, so the first row rests fully opaque
 * (clear of the ramp). MUST match the `fadePx` passed to fadeMaskY. */
const THREAD_FADE = 24;

/* Conversation-state animations (no Figma node — derived from the panel palette). */
const stopPulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${LUMIO.stopRing}; }
  70% { box-shadow: 0 0 0 6px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

const caretBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

/* Top inset of the "Lumio {area}" header tag. Sized so the tag's vertical centre
 * lands on the same line as the drawer's close button — which itself sits on the
 * TopBar control row (≈64px from the viewport top). Used as BOTH the welcome
 * Root's top pad and (mirrored top+bottom) the conversation header's pad, so the
 * tag is balanced and the close button reads as one of the header controls. */
const HEADER_TAG_INSET = '35px';

/* Figma `lumio` (4662:343): r-26, 1px white border, black→#151515 gradient.
 *
 * BOTH states are a single-cell grid: the header, the scrolling middle (the welcome
 * greeting + suggestions, or the conversation Thread) and the composer are STACKED in
 * the same cell. The middle fills the whole panel and scrolls UNDER the header/footer
 * (which sit on top via z-index); its fade mask dissolves the content into them. No
 * Root padding — each region owns its own inset, so the scroll region bleeds to the
 * panel edges while the header and composer stay fixed and only the content moves. */
export const Root = styled.aside`
  position: relative;
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  border-radius: 26px;
  border: 1px solid ${LUMIO.border};
  background: ${LUMIO.bg};
  overflow-x: hidden;
  display: grid;
  grid-template: 1fr / 1fr;
  overflow-y: hidden;
`;

/* 4662:344/345 — the fixed header band: just the logo row + actions (the greeting
 * now scrolls with the content, in Middle). Overlays the top of the scrolling middle
 * (same grid cell, z-index above it) with its own inset, so wheel/touch scroll passes
 * THROUGH it to the content (pointer-events:none; its buttons re-enable themselves)
 * and the middle's fade mask dissolves content into it as it scrolls up. Equal
 * top/bottom inset centres the "Lumio {area}" tag in the band, with the tag's centre
 * on the close-button line. Identical in both states. */
export const Top = styled.div`
  grid-area: 1 / 1;
  align-self: start;
  z-index: 2;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${HEADER_TAG_INSET} 24px;
  pointer-events: none;

  & button {
    pointer-events: auto;
  }
`;

/* 4662:345. */
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  height: 24px;
`;

/* 4662:346 — mark + word-mark, gap 6. */
export const Logo = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

export const LogoMark = styled.span`
  display: block;
  width: 24px;
  height: 24px;

  svg,
  img {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

/* 4662:348 — Satoshi Bold 15.273/1.15, gradient-clipped. */
export const LogoText = styled(Text).attrs({ tag: 'span' as const })`
  text-align: center;
  font-size: 15.273px;
  line-height: 1.15;
  font-weight: 700;
  background: ${LUMIO.logoText};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

/* 4662:355 — the tiny section tag: 8px/1.15, 4px after the word-mark (the row's
 * gap is 6 → -2px), top-aligned 10px down the 24px header. */
export const SectionTag = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 8px;
  line-height: 1.15;
  font-weight: 400;
  color: ${LUMIO.action};
  align-self: flex-start;
  margin: 10px 0 0 -2px;
  text-transform: lowercase;
`;

/* Header controls row. The action discs sit in 48px touch boxes (6px inset), so
 * the row's own gap stays tight, matching the mobile overlay's header spacing. */
export const Actions = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`;

/* No cursor affordance while the icons are decorative (no handlers yet). */
export const Action = styled.span`
  display: block;
  width: 16px;
  height: 16px;
  color: ${LUMIO.action};
`;

/* Shared Lumio header action (new / past conversations): a 36px frosted disc
 * centred in a 48px touch target. The SAME look on the desktop panel and the mobile
 * overlay; the mobile HeaderAction layers its entrance animation on top. Tap
 * feedback lives on the disc + glyph so it never fights a button-level transform. */
export const headerActionDisc = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${LUMIO_MOBILE.actionGlyph};
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${LUMIO_MOBILE.actionBg};
    transition: background ${ANIM.INTERACT_MS}ms ease, transform ${ANIM.INTERACT_MS}ms ease;
  }

  /* Keep the glyph above the disc. */
  svg {
    position: relative;
    transition: transform ${ANIM.INTERACT_MS}ms ease;
  }

  &:hover::before {
    background: ${LUMIO_MOBILE.actionBgHover};
  }
  &:active::before,
  &:active svg {
    transform: scale(0.92);
  }
`;

/* Shared Lumio close control: a 30px white disc with a dark X (mobile overlay and
 * desktop panel alike). */
export const headerCloseDisc = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${LUMIO_MOBILE.closeBg};
  color: ${LUMIO_MOBILE.closeGlyph};
  cursor: pointer;

  &:hover {
    background: ${LUMIO_MOBILE.closeBgHover};
  }
  &:active {
    transform: scale(0.92);
  }
`;

/* Desktop header action (new / past conversations), identical to the mobile one. */
export const ActionButton = styled.button`
  ${headerActionDisc}
`;

/* Desktop close ("Close Lumio"), identical to the mobile close disc. */
export const CloseButton = styled.button`
  ${headerCloseDisc}
  transition: background ${ANIM.INTERACT_MS}ms ease, transform ${ANIM.INTERACT_MS}ms ease;
`;

/* 4662:352 — greeting block, gap 24. */
export const Greeting = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  color: ${LUMIO.greeting};
`;

/* 4662:353 — 16px, regular. */
export const Hello = styled(Text).attrs({ tag: 'paragraph' as const })`
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  margin: 0;
`;

/* 4662:354 — 14/20 light, with a medium "Lumio". */
export const Intro = styled.p`
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
  margin: 0;
  width: 100%;
`;

export const IntroStrong = styled(Text).attrs({ tag: 'span' as const })`
  font-weight: 500;
`;

/* 4662:356 — the scrolling middle in the welcome / resume states: greeting +
 * question + suggestion sections (gap 24). Mirrors the conversation Thread — fills
 * the whole panel (same grid cell as the header/footer), scrolls under them, and its
 * fade mask dissolves the content into them. Top/bottom padding clears the measured
 * header / composer so the first row rests just below the header and the last rests
 * THREAD_END_GAP above the composer. */
export const Middle = styled.div<{ $headerH: number; $footerH: number }>`
  grid-area: 1 / 1;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;
  width: 100%;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${(props) => props.$headerH + THREAD_FADE}px 24px ${(props) => props.$footerH + THREAD_END_GAP}px;
  ${thinScrollbar}
  ${(props) => fadeMaskY(props.$headerH, props.$footerH, THREAD_FADE)}
`;

/* 4662:358. */
export const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

/* 4662:359 / 4662:366 — label + cards, gap 12. */
export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

/* 4662:360 / 4662:367 — icon + label, gap 6; colour set per section. */
export const SectionLabel = styled.div<{ $color: string }>`
  display: flex;
  gap: 6px;
  align-items: center;
  color: ${(props) => props.$color};
`;

/* 4662:362 / 4662:369 — 12/20 medium. */
export const SectionLabelText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
`;

/* 4662:370 — the DEEP DIVE pair, gap 12. */
export const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

/* 4662:363/371/374 — h-64 r-20 pink border, translucent pink→cyan fill. (The Figma
 * cyan inner glow on the right edge was dropped: over the pink border it rendered as
 * a doubled border line rather than the intended single cyan edge.) */
export const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 64px;
  width: 100%;
  padding: 0 12px;
  border-radius: 20px;
  border: 1px solid ${LUMIO.cardBorder};
  background: ${LUMIO.cardBg};
  cursor: pointer;
  text-align: left;
  transition: transform 120ms ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${LUMIO.cardBorder};
    outline-offset: 2px;
  }
`;

/* 4662:365/373/376 — 14/20 light, two lines max. On the light theme the copy steps
 * up to weight 400: pinned pure-black at 300 still read faint over the tinted fill. */
export const CardText = styled(Text).attrs({ tag: 'paragraph' as const })`
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
  margin: 0;
  width: 100%;
  color: ${LUMIO.cardText};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  :root[data-ows-theme='light'] & {
    font-weight: 400;
  }
`;

/* 4662:377 — the fixed composer band: label + pill in welcome, just the pill in
 * conversation (gap 12). Overlays the bottom of the scrolling middle (same grid cell,
 * z-index above it) with its own 24px inset, so content scrolls up behind it.
 * Identical in both states. */
export const InputBlock = styled.div`
  grid-area: 1 / 1;
  align-self: end;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 0 24px 24px;
`;

/* The prompt pill — now matches the mobile input pill exactly (per request): a
 * solid zinc surface with a hairline border + soft lift and the ringed send orb,
 * instead of the old gradient-border treatment. Seats the 48px orb (8px right). */
export const Pill = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  min-height: 64px;
  padding: 8px 8px 8px 22px;
  border-radius: 999px;
  background: ${LUMIO_MOBILE.pillBg};
  border: 1px solid ${LUMIO_MOBILE.pillBorder};
  box-shadow: ${LUMIO_MOBILE.pillShadow};
`;

/* 4662:380 — the live composer. The Figma node is a gradient-clipped *placeholder*
 * span; a functional input can't gradient-clip its typed value and stay readable,
 * so typed copy is solid (composerText) and the muted placeholder carries the
 * brand tint. Auto-grows up to ~4 lines, then scrolls. */
export const PromptInput = styled.textarea`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  color: ${LUMIO.composerText};
  font-family: inherit;
  font-size: 16px;
  font-weight: 300;
  line-height: 22px;
  /* One-line floor: even if the auto-grow JS measured a stale height:0px (e.g. it
   * ran while the docked column was still display:none), min-height keeps the
   * field at least one line tall, so the composer can never render invisible. */
  min-height: 22px;
  max-height: 104px;
  overflow-y: auto;
  scrollbar-width: thin;

  &::placeholder {
    color: ${LUMIO.composerPlaceholder};
  }
`;

/* The send orb — identical treatment to the mobile send orb (per request): a 48px
 * disc with a translucent magenta→blue radial fill, a masked brand-gradient ring,
 * and a white paper-plane glyph. Shares the LUMIO_MOBILE tokens so the two can't
 * drift. */
export const SendButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${LUMIO_MOBILE.orbBg};
  color: ${LUMIO_MOBILE.orbGlyph};
  backdrop-filter: blur(4px);

  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    padding: 2px;
    background: ${LUMIO_MOBILE.orbRing};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  &:active:not(:disabled) {
    transform: scale(0.94);
  }

  &:disabled {
    opacity: 0.55;
  }
`;

/* While the assistant is generating, the send orb becomes a Stop control — the
 * same 48px disc with a brand-gradient fill, a white square, and a working pulse. */
export const StopButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  padding: 0;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  background: ${LUMIO.stopBg};
  color: ${LUMIO.stopIcon};
  animation: ${stopPulse} ${LUMIO_CHAT.stopPulseMs}ms ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* ── Conversation thread ─────────────────────────────────────────────────── */

/* The message thread fills the WHOLE panel (same grid cell as the header/footer)
 * and scrolls under them. Root has no padding in conversation mode, so the Thread
 * spans edge to edge and its scrollbar sits flush to the panel's right border;
 * the 24px side padding re-insets the messages. Top/bottom padding clears the
 * header / composer (their measured heights, passed in) so the first message sits
 * just below the header and the last message rests THREAD_END_GAP (50px) above the
 * composer. The fade mask dissolves the content into the header/footer as it
 * scrolls past — a real CSS mask (theme/mixins fadeMaskY), so the effect is
 * self-contained and travels with the pane if it's reused elsewhere. */
export const Thread = styled.div<{ $headerH: number; $footerH: number }>`
  grid-area: 1 / 1;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-sizing: border-box;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${(props) => props.$headerH + THREAD_FADE}px 24px ${(props) => props.$footerH + THREAD_END_GAP}px;
  ${thinScrollbar}
  ${(props) => fadeMaskY(props.$headerH, props.$footerH, THREAD_FADE)}
`;

export const MessageRow = styled.div<{ $role: 'user' | 'assistant' }>`
  display: flex;
  width: 100%;
  justify-content: ${(props) => (props.$role === 'user' ? 'flex-end' : 'flex-start')};
`;

/* User turn — right-aligned bubble. Plain styled.div: the Lumio markdown
 * helpers (plainToHtml/markdownToHtml) build pre-escaped HTML that we render via
 * dangerouslySetInnerHTML at the call site — NOT through <Text> (which now
 * escapes its child, so it would show raw tags). */
export const UserBubble = styled.div`
  max-width: 86%;
  padding: 10px 14px;
  border-radius: 16px 16px 4px 16px;
  background: ${LUMIO.userBubbleBg};
  border: 1px solid ${LUMIO.userBubbleBorder};
  color: ${LUMIO.userBubbleText};
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

/* Assistant turn — full-width column holding the markdown body + any meta note. */
export const Assistant = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 100%;
`;

/* Markdown body. Plain styled.div rendered via dangerouslySetInnerHTML at the
 * call site (markdownToHtml output) — NOT through <Text> (which now escapes).
 * Styles target the tags emitted by markdownToHtml; the trailing `.lc-caret` is
 * the streaming cursor injected at the live insertion point. */
export const MarkdownText = styled.div`
  color: ${LUMIO.assistantText};
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
  word-break: break-word;
  overflow-wrap: anywhere;

  & > *:first-child {
    margin-top: 0;
  }
  & > *:last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0 0 10px;
  }
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 14px 0 8px;
    line-height: 1.25;
    font-weight: 600;
    color: ${LUMIO.assistantStrong};
  }
  h2 {
    font-size: 16px;
  }
  h3 {
    font-size: 15px;
  }
  h4,
  h5,
  h6 {
    font-size: 14px;
  }
  strong {
    font-weight: 600;
    color: ${LUMIO.assistantStrong};
  }
  em {
    font-style: italic;
  }
  a {
    color: ${LUMIO.link};
    text-decoration: underline;
  }
  ul,
  ol {
    margin: 0 0 10px;
    padding-left: 18px;
  }
  li {
    margin: 2px 0;
  }
  li::marker {
    color: ${LUMIO.bullet};
  }
  code {
    font-family: Satoshi, ui-sans-serif, system-ui, sans-serif;
    font-size: 12.5px;
    background: ${LUMIO.codeBg};
    border: 1px solid ${LUMIO.codeBorder};
    border-radius: 5px;
    padding: 1px 5px;
  }
  pre {
    margin: 0 0 10px;
    padding: 10px 12px;
    background: ${LUMIO.codeBg};
    border: 1px solid ${LUMIO.codeBorder};
    border-radius: 10px;
    overflow-x: auto;
  }
  pre code {
    background: none;
    border: none;
    padding: 0;
    color: ${LUMIO.codeText};
    white-space: pre;
  }
  blockquote {
    margin: 0 0 10px;
    padding: 4px 0 4px 12px;
    border-left: 2px solid ${LUMIO.quoteBar};
    color: ${LUMIO.quoteText};
  }
  hr {
    border: none;
    border-top: 1px solid ${LUMIO.divider};
    margin: 12px 0;
  }

  .lc-caret {
    display: inline-block;
    width: 6px;
    height: 14px;
    margin-left: 1px;
    border-radius: 1px;
    vertical-align: text-bottom;
    background: ${LUMIO.caret};
    animation: ${caretBlink} ${LUMIO_CHAT.caretMs}ms steps(1, end) infinite;
  }
`;

/* "Thinking" indicator — three bouncing dots before the first token arrives. */
export const Typing = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 2px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${LUMIO.typingDot};
    animation: ${dotBounce} ${LUMIO_CHAT.typingDotMs}ms ease-in-out infinite;
  }
  span:nth-child(2) {
    animation-delay: ${LUMIO_CHAT.typingDotMs / 8}ms;
  }
  span:nth-child(3) {
    animation-delay: ${LUMIO_CHAT.typingDotMs / 4}ms;
  }
`;

/* "Stopped" note under an aborted turn. */
export const MetaNote = styled(Text)`
  font-size: 12px;
  line-height: 16px;
  color: ${LUMIO.metaText};
`;

/* Error + retry row under a failed turn. */
export const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ErrorText = styled(Text)`
  font-size: 12px;
  line-height: 16px;
  color: ${LUMIO.errorText};
`;

export const RetryButton = styled.button`
  appearance: none;
  padding: 3px 10px;
  border: 1px solid ${LUMIO.divider};
  border-radius: 999px;
  background: transparent;
  color: ${LUMIO.assistantText};
  font-size: 12px;
  line-height: 16px;
  cursor: pointer;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}

  &:hover {
    background: ${LUMIO.codeBg};
  }
`;

/* ── Proposal decision chips ───────────────────────────────────────────────
 * A proposal's title and its producer-declared decision chips, shown as a card
 * in the proposals inbox (see ProposalCard below). A chip click decides the
 * proposal deterministically (no chat turn). Accept chips take the pink card
 * accent, deny chips the muted action grey.
 */
export const ProposalTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  line-height: 18px;
  color: ${LUMIO.greeting};
`;

/* The producer's plain-language rationale under the title — what was flagged and
 * the current state (e.g. "the player was temporarily banned; keep it or lift
 * it") — so the owner decides on the reasoning, not just the headline. */
export const ProposalSummary = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  font-size: 12.5px;
  line-height: 18px;
  color: ${LUMIO.action};
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

/* A proposal decision chip in one of three visual tiers, so a proposal that
 * offers several actions reads with a clear hierarchy instead of a wall of
 * identical pills:
 *   - `$primary` (the first accept action) — filled brand gradient, the CTA.
 *   - accept (other accept variants) — pink outline, secondary.
 *   - `$deny` — muted ghost.
 */
export const Chip = styled.button<{ $deny?: boolean; $primary?: boolean }>`
  appearance: none;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$primary ? 'transparent' : p.$deny ? LUMIO.divider : LUMIO.cardBorder)};
  background: ${(p) => (p.$primary ? LUMIO.stopBg : 'transparent')};
  color: ${(p) => (p.$primary ? LUMIO.stopIcon : p.$deny ? LUMIO.action : LUMIO.greeting)};
  font-size: 12px;
  font-weight: ${(p) => (p.$primary ? 600 : 400)};
  line-height: 16px;
  cursor: pointer;
  ${hoverWash}

  &:hover {
    background: ${(p) => (p.$primary ? LUMIO.stopBg : LUMIO.codeBg)};
    ${(p) => (p.$primary ? 'filter: brightness(1.08);' : '')}
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/* ── Proactive briefing ────────────────────────────────────────────────────
 * The idle panel's "for you" digest: a proposals count badge in the header, the
 * Needs-you card (proposals), the docked nudge ("Noticed"), and route-aware "On
 * this page" chips. No Figma node — derived from the panel palette (see the LUMIO
 * proactive-briefing tokens). All sit in the scrolling Middle, below the greeting.
 */

/* One briefing section: its eyebrow + the block beneath it, kept tight (the
 * Middle's own gap:24 separates one section from the next). */
export const BriefSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 100%;
`;

/* Header proposals tray + its unread-style count badge. Wider box than the 16px
 * ActionButton so the 18px tray glyph and the overflowing badge both fit; the
 * badge is absolutely placed so it can bleed past the glyph's corner. */
export const TrayButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${LUMIO.action};
  cursor: pointer;

  &:hover {
    color: ${LUMIO.greeting};
  }
`;

export const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  box-sizing: border-box;
  border-radius: 999px;
  background: ${LUMIO.badgeBg};
  color: ${LUMIO.stopIcon};
  font-size: 10px;
  font-weight: 700;
  line-height: 15px;
  text-align: center;
  /* A ring in the panel colour so the badge reads as lifted off the glyph. */
  box-shadow: 0 0 0 2px ${LUMIO.badgeRing};
`;

/* Section eyebrow: a coloured dot + an all-caps label. Colour carries the intent
 * (pink = act / needs you, cyan = inform / noticed, blue = page). */
export const Eyebrow = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 2px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(props) => props.$color};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: 0 0 8px currentColor;
  }
`;

/* Needs-you card — the proposals digest. An actionable pink pull that opens the
 * inbox; count + a preview of the first titles. */
export const NeedsCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 15px;
  border-radius: 16px;
  border: 1px solid ${LUMIO.needsBorder};
  background: ${LUMIO.needsBg};
  color: ${LUMIO.greeting};
  text-align: left;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ${ACQ.ease}, border-color ${ANIM.INTERACT_MS}ms ${ACQ.ease};

  &:hover {
    background: ${LUMIO.needsBgHover};
    border-color: ${LUMIO.needsBorderHover};
  }

  &:focus-visible {
    outline: 2px solid ${LUMIO.link};
    outline-offset: 2px;
  }
`;

export const NeedsIcon = styled.span`
  display: grid;
  place-items: center;
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: ${LUMIO.needsIconBg};
  color: ${LUMIO.stopIcon};
`;

export const NeedsBody = styled.span`
  flex: 1;
  min-width: 0;
`;

export const NeedsTitle = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  font-size: 14.5px;
  font-weight: 600;
  line-height: 19px;
  color: ${LUMIO.greeting};
`;

export const NeedsSub = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  margin-top: 2px;
  font-size: 12px;
  line-height: 16px;
  color: ${LUMIO.action};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const NeedsChevron = styled.span`
  display: flex;
  flex: none;
  color: ${LUMIO.action};
`;

/* Noticed card — the docked nudge. A cyan-edged inform surface; its optional
 * action chip is the on-ramp into a real turn (Act nudges only). */
export const NoticedCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 40px 14px 15px;
  border-radius: 16px;
  border: 1px solid ${LUMIO.noticedBorder};
  background: ${LUMIO.noticedBg};
`;

export const NoticedText = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  font-size: 13.5px;
  font-weight: 400;
  line-height: 20px;
  color: ${LUMIO.greeting};
`;

export const NoticedDismiss = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: ${LUMIO.action};
  font-size: 16px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: ${LUMIO.greeting};
    background: ${LUMIO.codeBg};
  }
`;

/* An action chip on the docked nudge — brand-pink outline, like a suggestion. */
export const NoticedAction = styled.button`
  align-self: flex-start;
  appearance: none;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${LUMIO.cardBorder};
  background: transparent;
  color: ${LUMIO.greeting};
  font-size: 12.5px;
  line-height: 16px;
  cursor: pointer;
  ${hoverWash}

  &:hover {
    background: ${LUMIO.codeBg};
  }
`;

/* "On this page" — route-aware chips. Picking one sends a real chat turn. */
export const PageChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const PageChip = styled.button`
  appearance: none;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${LUMIO.divider};
  background: ${LUMIO.codeBg};
  color: ${LUMIO.assistantText};
  font-size: 13px;
  line-height: 16px;
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms ${ACQ.ease}, border-color ${ANIM.INTERACT_MS}ms ${ACQ.ease}, background ${ANIM.INTERACT_MS}ms ${ACQ.ease};

  &:hover {
    color: ${LUMIO.assistantStrong};
    border-color: ${LUMIO.pageChipHoverBorder};
    background: ${LUMIO.pageChipHoverBg};
  }

  &:focus-visible {
    outline: 2px solid ${LUMIO.link};
    outline-offset: 2px;
  }
`;

/* ── Proposals inbox ───────────────────────────────────────────────────────
 * The dedicated proposals screen the header tray / Needs-you card opens. A back
 * header + a scrolling list of proposal cards (title + decision chips). Replaces
 * the old thread-tail ProposalBlock — proposals now live in one reachable place.
 */
export const InboxHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

export const BackButton = styled.button`
  display: grid;
  place-items: center;
  flex: none;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${LUMIO.divider};
  border-radius: 10px;
  background: ${LUMIO.codeBg};
  color: ${LUMIO.assistantText};
  cursor: pointer;

  &:hover {
    color: ${LUMIO.assistantStrong};
    background: ${LUMIO.userBubbleBg};
  }
`;

export const InboxTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 16px;
  font-weight: 650;
  color: ${LUMIO.greeting};
`;

export const InboxCount = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${LUMIO.action};
`;

export const ProposalCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 15px;
  border-radius: 16px;
  border: 1px solid ${LUMIO.divider};
  background: ${LUMIO.cardSurface};
`;

/* Empty-inbox line, shown when the tray is opened with nothing pending. */
export const InboxEmpty = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  font-size: 13.5px;
  line-height: 20px;
  color: ${LUMIO.action};
`;
